import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import { mapLevel } from '../utils/languageUtils';
import './BrainWarmup.css';
import './ExamPrep.css';
import './JapaneseExamPrep.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';
const JLPT_LEVELS = ['N5', 'N4', 'N3', 'N2', 'N1'];

// Format total minutes into "2 hours 10 min" / "55 min".
function formatDuration(mins) {
  const m = Number(mins) || 0;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h > 0 && rem > 0) return `${h} ${h === 1 ? 'hour' : 'hours'} ${rem} min`;
  if (h > 0) return `${h} ${h === 1 ? 'hour' : 'hours'}`;
  return `${rem} min`;
}

// N5 fallback (used only if the config fetch fails) so the page always renders.
const FALLBACK_CONFIG = {
  level: 'N5',
  totalDurationMinutes: 130,
  totalScore: 180,
  overallPassing: 80,
  sequenceLabel: 'Characters & Vocabulary → Grammar and Reading → Listening.',
  description:
    "A full-length simulation of the Japanese Language Proficiency Test N5. This practice exam is designed to simulate the format and timing of the official exam. Find a quiet place, ensure you have a stable internet connection, and begin only when you're ready. Complete all three sections in order. Results are generated only after all sections are submitted.",
  sections: [
    { sectionCode: 'A', sectionName: 'Vocabulary & Characters', durationMinutes: 25, numQuestions: 35, totalScore: 60, passingScore: 19, passingNote: null, breakAfterMinutes: 10, order: 1 },
    { sectionCode: 'B', sectionName: 'Grammar & Reading', durationMinutes: 55, numQuestions: 32, totalScore: 60, passingScore: 19, passingNote: null, breakAfterMinutes: 10, order: 2 },
    { sectionCode: 'C', sectionName: 'Listening', durationMinutes: 30, numQuestions: 24, totalScore: 60, passingScore: 19, passingNote: null, breakAfterMinutes: 0, order: 3 },
  ],
  totalQuestions: 91,
};

const JapaneseExamPrep = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  usePageTimeTracker('exam-prep', user);

  const { language: stateLanguage = 'ja', proficiencyLevel: stateLevel = 'beginner-n5' } = location.state || {};
  const [language] = useState(stateLanguage);
  const [proficiencyLevel, setProficiencyLevel] = useState(stateLevel);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);
  const [cfg, setCfg] = useState(FALLBACK_CONFIG);
  const [attemptsLeft, setAttemptsLeft] = useState(2); // per-level remaining (2/24h)
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');

  const levelCode = mapLevel(proficiencyLevel); // e.g. 'N5'

  // Fetch the JLPT config for the selected level from the DB. Falls back to the N5
  // config if the request fails so the page always renders.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/japanese-exam/config?level=${levelCode}&_t=${Date.now()}`, { withCredentials: true })
      .then(res => {
        if (!cancelled && res.data && res.data.success && res.data.config) setCfg(res.data.config);
      })
      .catch(() => { if (!cancelled) setCfg(FALLBACK_CONFIG); });
    return () => { cancelled = true; };
  }, [levelCode]);

  // Check remaining attempts for THIS level (2 per 24h, per user + level). Re-runs
  // whenever the selected level changes so the message/gate stays accurate.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/japanese-exam/check-attempts?level=${levelCode}&_t=${Date.now()}`, {
      withCredentials: true, headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
    })
      .then(res => {
        if (!cancelled && res.data && res.data.success && typeof res.data.attempts_remaining === 'number') {
          setAttemptsLeft(res.data.attempts_remaining);
        }
      })
      .catch(() => { /* don't block the UI on error */ });
    return () => { cancelled = true; };
  }, [levelCode]);

  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest('.ep-intro-level-select')) setShowLevelDropdown(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Map a short JLPT code back to the level slug used across the app.
  const codeToSlug = { N5: 'beginner-n5', N4: 'elementary-n4', N3: 'pre-intermediate-n3', N2: 'intermediate-n2', N1: 'advanced-n1' };

  // Derived display values from the fetched config.
  const totalQuestions = cfg.totalQuestions || (cfg.sections || []).reduce((s, x) => s + (x.numQuestions || 0), 0);
  const durationLabel = formatDuration(cfg.totalDurationMinutes);
  const totalMarks = cfg.totalScore || 0;
  const passingMarks = cfg.overallPassing || 0;
  const minPerSection = (cfg.sections && cfg.sections[0] && cfg.sections[0].passingScore) || 0;
  const passPct = totalMarks > 0 ? Math.round((passingMarks / totalMarks) * 10000) / 100 : 0;

  const canAttempt = attemptsLeft > 0;

  const handleStart = async () => {
    if (!confirmChecked || !canAttempt || generating) return;
    setGenError('');
    setGenerating(true);
    try {
      // GENERATE the exam here (on Start Exam Prep) — this does NOT create the
      // attempt yet. The attempt is created when the user clicks Start Section A
      // (the question page calls /start with this gen_token).
      const res = await axios.post(`${API_BASE}/japanese-exam/generate`, { level: levelCode }, { withCredentials: true });
      if (res.data && res.data.success) {
        // In-memory sentinel: marks that we're entering the questions page via a
        // real in-app navigation (not a page reload). It survives SPA navigation
        // but is wiped on a hard refresh, letting the questions page tell a fresh
        // start from a reload (where the browser preserves location.state).
        try { window.__jeqSessionAlive = true; } catch (e) { /* noop */ }
        navigate('/japanese-exam-questions', {
          state: {
            language: 'ja',
            proficiencyLevel,
            exam: res.data.exam,
            genToken: res.data.gen_token,
            attemptId: res.data.attempt_id,
            // This attempt was just counted, so one fewer is left for today.
            attemptsLeft: Math.max(0, attemptsLeft - 1),
          },
        });
      } else {
        setGenError(res.data?.error || 'Failed to start the exam. Please try again.');
        setGenerating(false);
      }
    } catch (err) {
      setGenError(err.response?.data?.error || 'Failed to start the exam. Please try again.');
      setGenerating(false);
    }
  };

  return (
    <div className="exam-prep-page ep-intro-page jep-page">
      <Header user={user} />
      <SideNav />

      <main className="ep-intro-main">
        {/* Top block */}
        <div className="ep-intro-top">
          <h1 className="ep-intro-heading">
            Ready for Your <span className="ep-intro-heading-purple">Japanese {levelCode} Exam</span> Prep?
          </h1>
          <p className="ep-intro-desc">{cfg.description}</p>
          <p className="ep-intro-sequence">
            Sequence will be: <span>{cfg.sequenceLabel}</span>
          </p>
        </div>

        {/* Middle band */}
        <div className="ep-intro-band">
          <div className="ep-intro-chips">
            <div className="ep-intro-chip"><img src="/images/help.svg" alt="" width="16" height="16" onError={e => { e.target.style.display = 'none'; }} /><span>{totalQuestions} questions</span></div>
            <div className="ep-intro-chip"><img src="/images/Time-black.svg" alt="" width="16" height="16" onError={e => { e.target.style.display = 'none'; }} /><span>{durationLabel}</span></div>
            <div className="ep-intro-chip"><img src="/images/icon-assignment.svg" alt="" width="16" height="16" onError={e => { e.target.style.display = 'none'; }} /><span>{totalMarks} marks</span></div>
            <div className="ep-intro-chip"><img src="/images/passingmarksicon.svg" alt="" width="16" height="16" onError={e => { e.target.style.display = 'none'; }} /><span>Passing Marks {passingMarks}/{totalMarks} ({passPct}%)</span></div>
            <div className="ep-intro-chip"><img src="/images/passingmarksicon.svg" alt="" width="16" height="16" onError={e => { e.target.style.display = 'none'; }} /><span>Min {minPerSection} marks per section</span></div>
            <div className={'ep-intro-chip' + (attemptsLeft >= 2 ? '' : attemptsLeft === 1 ? ' jep-attempts-warn' : ' jep-attempts-none')}>
              <img src="/images/Strategy.svg" alt="" width="16" height="16" onError={e => { e.target.style.display = 'none'; }} />
              <span>{attemptsLeft >= 2
                ? <><strong>2</strong> attempts every 24 hours</>
                : <><strong>{attemptsLeft}</strong> attempts left for today</>}</span>
            </div>
          </div>

          <div className="ep-intro-band-inner">
            <div className="ep-intro-columns">
              <div className="ep-intro-col">
                <h3 className="ep-intro-col-title">Exam Guidelines</h3>
                <ul className="ep-intro-list jep-list">
                  <li>The JLPT N5 mock test consists of three sections:
                    <ul className="jep-sublist">
                      <li><strong>1. Section A:</strong> Language Knowledge (Characters &amp; Vocabulary) &ndash; 35 questions, 25 minutes.</li>
                      <li><strong>2. Section B:</strong> Language Knowledge (Grammar &amp; Reading) &ndash; 32 questions, 55 minutes.</li>
                      <li><strong>3. Section C:</strong> Listening &ndash; 24 questions, approximately 30 minutes.</li>
                    </ul>
                  </li>
                  <li>Important: A total score of 80+ is required, with a minimum of 19 in each section to pass.</li>
                  <li>All questions are <strong>multiple-choice questions (MCQs).</strong></li>
                  <li>Read each question carefully before answering and select one answer from the options provided.</li>
                </ul>
              </div>
              <div className="ep-intro-col-divider"></div>
              <div className="ep-intro-col">
                <h3 className="ep-intro-col-title">Things to Remember</h3>
                <ul className="ep-intro-list jep-list">
                  <li>Keep track of the timer and submit each section before the time expires.</li>
                  <li>Each section has its own time limit and will be automatically submitted when the time ends.</li>
                  <li>Once submitted, a section cannot be reopened or its answers changed.</li>
                  <li>A maximum 10-minute break is allowed between sections. You must start the next section within this time.</li>
                  <li>Exiting the exam before submission will end your attempt, and your progress will not be saved.</li>
                  <li>Your answers are submitted automatically when time ends, and the exam cannot be paused once started.</li>
                  <li>Complete the exam in one sitting and review your answers before submitting.</li>
                  <li>Ensure your device is fully charged or connected to a power source.</li>
                </ul>
              </div>
            </div>

            {/* Selectors */}
            <div className="ep-intro-selectors">
              <div className="ep-intro-field">
                <label className="ep-intro-field-label">Language</label>
                <div className="ep-intro-lang-select">
                  <span className="ep-intro-lang-current">Japanese |</span>
                  <button className="ep-intro-lang-change" onClick={() => navigate('/explore-learning', { state: { language, proficiencyLevel } })}>
                    Change Language
                    <img src="/images/purplearrow.svg" alt="" width="14" height="14" onError={e => { e.target.style.display = 'none'; }} />
                  </button>
                </div>
              </div>
              <div className="ep-intro-field">
                <label className="ep-intro-field-label">Proficiency Level</label>
                <div className="ep-intro-level-select">
                  <button className="ep-intro-level-btn" onClick={() => setShowLevelDropdown(!showLevelDropdown)}>
                    <span className="ep-intro-level-left"><strong>Japanese</strong> - {levelCode}</span>
                    <img src="/images/dropdownarrow.svg" alt="" width="12" height="12" onError={e => { e.target.style.display = 'none'; }} />
                  </button>
                  {showLevelDropdown && (
                    <div className="ep-intro-level-dropdown">
                      {JLPT_LEVELS.map(lvl => {
                        // N1 and N2 are not available yet — show them disabled.
                        const disabled = lvl === 'N1' || lvl === 'N2';
                        return (
                          <button key={lvl}
                            className={'ep-intro-level-item' + (levelCode === lvl ? ' selected' : '') + (disabled ? ' inactive' : '')}
                            disabled={disabled}
                            onClick={() => { if (!disabled) { setProficiencyLevel(codeToSlug[lvl]); setShowLevelDropdown(false); } }}>
                            Japanese - {lvl}{disabled ? ' (Coming soon)' : ''}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="ep-intro-lang-hint">
              <img src="/images/black-info.svg" alt="" width="14" height="14" onError={e => { e.target.style.display = 'none'; }} />
              <span>You have already selected Japanese, you can continue or change language here</span>
            </div>

            <label className="ep-intro-checkbox">
              <input type="checkbox" checked={confirmChecked} onChange={e => setConfirmChecked(e.target.checked)} />
              <span>I understand my language selection cannot be changed once the challenge begins</span>
            </label>
          </div>
        </div>

        {/* Start button */}
        <div className="ep-intro-start-wrap">
          <button className="ep-btn-start" onClick={handleStart} disabled={!confirmChecked || !canAttempt || generating}>
            {generating ? 'Preparing your exam...' : 'Start Exam Prep'}
            {!generating && <img src="/images/arrow-right.svg" alt="" width="20" height="20" onError={e => { e.target.style.display = 'none'; }} />}
          </button>
          {genError && <p className="ep-intro-attempts-msg">{genError}</p>}
        </div>
      </main>

      <Footer />

      {/* Loader popup while the agent generates the exam (same as other exam pages). */}
      {generating && (
        <div className="bw-confirm-overlay">
          <div className="ep-gen-loading">
            <img src="/images/loadingicon.svg" alt="" className="ep-submitting-icon" />
            <h3 className="ep-gen-loading-title">Hold on a moment!</h3>
            <p className="ep-gen-loading-text">We&#8217;re getting your questions ready. Your exam is almost ready to begin.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default JapaneseExamPrep;
